import { DragDropContext, DropResult } from '@hello-pangea/dnd';
import {
  alpha,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, deleteDoc, doc, getDocs, query, setDoc } from 'firebase/firestore';
import { db } from 'config/firebase';
import { useQueryClient } from '@tanstack/react-query';
import type { InventoryLead } from '@otw/models/leads/inventoryLead';
import { INVENTORY_LEAD_DEFAULT_STATUSES } from '@otw/models/leads/inventoryLead';
import { useInventoryLeads } from 'hooks/admin/useInventoryLeads';
import { useContacts } from 'hooks/contacts/useContacts';
import { useContactCompanies } from 'hooks/contacts/useContactCompanies';
import { useAuth } from 'contexts/AuthContext';
import { useIsMobile } from 'hooks/screen/useIsMobile';
import { useUsers } from 'hooks/useUsers';
import { generateRandomColor } from 'core/utils/colorUtils';
import { resolveInventoryRepName } from '../util/inventoryRepDisplay';
import {
  filterInventoryLeads,
  type InventoryLeadActiveFilters,
} from '../util/inventoryLeadFilters';
import {
  INVENTORY_BORDER_COLORS,
  INVENTORY_STATUS_NAMES,
  isDefaultInventoryLeadStatus,
} from '../util/inventoryStatusHelpers';
import { InventoryLeadList } from './cards';
import { MobileRecordCard } from 'components/MobileRecordCard';
import { MobileRecordList } from 'components/MobileRecordList';
import { inventoryLeadStatusTone } from 'components/StatusChip';
import InventoryLeadDetailDialog from './dialogs/InventoryLeadDetailDialog';
import InventoryLeadFormDialog from './dialogs/InventoryLeadFormDialog';
import InventoryLeadLostReasonDialog, {
  type InventoryLeadLostReasonSelection,
} from './dialogs/InventoryLeadLostReasonDialog';
import type { CustomInventoryLeadColumn } from 'hooks/admin/useInventoryLeadColumns';

export type InventoryBoardStats = {
  total: number;
  active: number;
  won: number;
  lost: number;
};

interface LocalColumn {
  id: string;
  leads: InventoryLead[];
  name: string;
  color: string;
}

interface LocalState {
  columns: Record<string, LocalColumn>;
  columnOrder: string[];
}

const DEFAULT_COLUMNS: string[] = [...INVENTORY_LEAD_DEFAULT_STATUSES];
const HIDDEN_BY_DEFAULT = new Set(['won', 'lost']);

const createInitialState = (
  leads: InventoryLead[],
  customColumns: CustomInventoryLeadColumn[],
): LocalState => {
  const columns: Record<string, LocalColumn> = {};

  DEFAULT_COLUMNS.forEach((status) => {
    columns[status] = {
      id: status,
      leads: [],
      name: INVENTORY_STATUS_NAMES[status as keyof typeof INVENTORY_STATUS_NAMES],
      color: INVENTORY_BORDER_COLORS[status as keyof typeof INVENTORY_BORDER_COLORS],
    };
  });

  customColumns.forEach((column) => {
    columns[column.id] = {
      id: column.id,
      leads: [],
      name: column.name,
      color: column.color,
    };
  });

  leads.forEach((lead) => {
    const status = lead.status || 'pendingList';
    if (!columns[status]) {
      columns[status] = {
        id: status,
        leads: [],
        name: INVENTORY_STATUS_NAMES[status as keyof typeof INVENTORY_STATUS_NAMES] || status,
        color:
          INVENTORY_BORDER_COLORS[status as keyof typeof INVENTORY_BORDER_COLORS] ||
          generateRandomColor(),
      };
    }
    columns[status].leads.push(lead);
  });

  const columnOrder = [...DEFAULT_COLUMNS];
  [...customColumns]
    .sort((a, b) => a.position - b.position)
    .forEach((column) => {
      if (!columnOrder.includes(column.id)) {
        const position = Math.min(column.position, columnOrder.length);
        columnOrder.splice(position, 0, column.id);
      }
    });
  Object.keys(columns).forEach((columnId) => {
    if (!columnOrder.includes(columnId)) columnOrder.push(columnId);
  });

  return { columns, columnOrder };
};

const doesLeadMatchSearch = (
  lead: InventoryLead,
  query: string,
  contactName: string,
  companyName: string,
  repName: string,
): boolean => {
  const q = query.toLowerCase().trim();
  if (!q) return true;
  if (lead.leadNumber?.toLowerCase().includes(q)) return true;
  if (contactName.toLowerCase().includes(q)) return true;
  if (companyName.toLowerCase().includes(q)) return true;
  if (lead.assignedRepId && repName.toLowerCase().includes(q)) return true;
  return (lead.trucks || []).some((truck) =>
    `${truck.make} ${truck.model}`.toLowerCase().includes(q),
  );
};

const computeStats = (boardState: LocalState): InventoryBoardStats => {
  let total = 0;
  let active = 0;
  let won = 0;
  let lost = 0;
  boardState.columnOrder.forEach((columnId) => {
    const column = boardState.columns[columnId];
    if (!column) return;
    const count = column.leads?.length || 0;
    total += count;
    if (columnId === 'won') won += count;
    else if (columnId === 'lost') lost += count;
    else active += count;
  });
  return { total, active, won, lost };
};

export interface InventoryLeadsBoardProps {
  /** Shared search query from the Leads tab chrome. */
  searchQuery: string;
  /** Shared show closed/lost (won/lost) toggle from the Leads tab chrome. */
  showHiddenLeads: boolean;
  /** Inventory-specific filters from the Leads tab chrome. */
  activeFilters?: InventoryLeadActiveFilters;
  /** Column ids hidden via the inventory filter dialog. */
  hiddenColumnIds?: string[];
  /** Increment to open the new-column dialog. */
  openColumnDialogSignal?: number;
  onStatsChange?: (stats: InventoryBoardStats) => void;
}

const InventoryLeadsBoard: React.FC<InventoryLeadsBoardProps> = ({
  searchQuery,
  showHiddenLeads,
  activeFilters = {},
  hiddenColumnIds = [],
  openColumnDialogSignal = 0,
  onStatsChange,
}) => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const { userInfo } = useAuth();

  const { inventoryLeads, updateInventoryLead, deleteInventoryLead } = useInventoryLeads();
  const { contacts } = useContacts();
  const { companies } = useContactCompanies();
  const { users } = useUsers();

  const [customColumns, setCustomColumns] = useState<CustomInventoryLeadColumn[]>([]);
  const [state, setState] = useState<LocalState>({ columns: {}, columnOrder: [] });

  const [isColumnDialogOpen, setIsColumnDialogOpen] = useState(false);
  const [newColumnTitle, setNewColumnTitle] = useState('');
  const [newColumnPosition, setNewColumnPosition] = useState(0);
  const [isDeleteColumnDialogOpen, setIsDeleteColumnDialogOpen] = useState(false);
  const [columnToDelete, setColumnToDelete] = useState<string | null>(null);
  const [targetColumnForLeads, setTargetColumnForLeads] = useState('');
  const [selectedLead, setSelectedLead] = useState<InventoryLead | null>(null);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [editingLead, setEditingLead] = useState<InventoryLead | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [pendingLostLead, setPendingLostLead] = useState<InventoryLead | null>(null);
  const [leadToDelete, setLeadToDelete] = useState<InventoryLead | null>(null);
  const [isDeleteLeadDialogOpen, setIsDeleteLeadDialogOpen] = useState(false);

  const contactsById = useMemo(() => new Map(contacts.map((c) => [c.id, c])), [contacts]);
  const companiesById = useMemo(() => new Map(companies.map((c) => [c.id, c])), [companies]);
  const getContactName = useCallback(
    (contactId: string) => contactsById.get(contactId)?.name || 'Unknown contact',
    [contactsById],
  );
  const getCompanyName = useCallback(
    (companyId: string) => companiesById.get(companyId)?.name || 'No company',
    [companiesById],
  );

  const loadCustomColumns = useCallback(async () => {
    try {
      const snapshot = await getDocs(query(collection(db, 'inventoryLeadColumns')));
      const loaded = snapshot.docs.map((d) => d.data() as CustomInventoryLeadColumn);
      setCustomColumns(loaded);
      return loaded;
    } catch (error) {
      console.error('Error loading inventory lead columns:', error);
      return [];
    }
  }, []);

  useEffect(() => {
    loadCustomColumns();
  }, [loadCustomColumns]);

  useEffect(() => {
    if (openColumnDialogSignal > 0) {
      setNewColumnTitle('');
      setNewColumnPosition(state.columnOrder.length);
      setIsColumnDialogOpen(true);
    }
    // intentionally only react to signal changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openColumnDialogSignal]);

  const leadsForBoard = useMemo(() => {
    if (!inventoryLeads) return [];
    let base = inventoryLeads;
    if (!showHiddenLeads) {
      base = base.filter((lead) => !HIDDEN_BY_DEFAULT.has(lead.status || ''));
    }
    if (searchQuery.trim()) {
      base = base.filter((lead) =>
        doesLeadMatchSearch(
          lead,
          searchQuery,
          getContactName(lead.contactId),
          getCompanyName(lead.companyId),
          resolveInventoryRepName(users, lead.assignedRepId),
        ),
      );
    }
    return filterInventoryLeads(base, activeFilters);
  }, [
    inventoryLeads,
    showHiddenLeads,
    searchQuery,
    activeFilters,
    getContactName,
    getCompanyName,
    users,
  ]);

  useEffect(() => {
    const next = createInitialState(leadsForBoard, customColumns);
    setState(next);
    onStatsChange?.(computeStats(next));
  }, [leadsForBoard, customColumns, onStatsChange]);

  const visibleColumnOrder = useMemo(
    () => state.columnOrder.filter((columnId) => !hiddenColumnIds.includes(columnId)),
    [state.columnOrder, hiddenColumnIds],
  );

  const applyStatusUpdate = async (
    lead: InventoryLead,
    nextStatus: string,
    lostReason?: InventoryLeadLostReasonSelection,
  ) => {
    await updateInventoryLead({
      id: lead.id,
      updates: {
        status: nextStatus,
        ...(nextStatus === 'lost' && lostReason
          ? {
              lostReasonId: lostReason.reasonId,
              lostReasonLabel: lostReason.reasonLabel,
              lostReasonNotes: lostReason.notes,
            }
          : {}),
      },
    });
  };

  const onDragEnd = async (result: DropResult) => {
    const { destination, source, draggableId } = result;
    if (
      !destination ||
      (destination.droppableId === source.droppableId && destination.index === source.index)
    ) {
      return;
    }

    const draggedLead = inventoryLeads?.find((lead) => lead.id === draggableId);
    if (!draggedLead) return;

    if (destination.droppableId === 'lost' && draggedLead.status !== 'lost') {
      setPendingLostLead(draggedLead);
      return;
    }

    try {
      const newState: LocalState = {
        columns: { ...state.columns },
        columnOrder: state.columnOrder,
      };

      const sourceColumn = { ...newState.columns[source.droppableId] };
      sourceColumn.leads = sourceColumn.leads.filter((lead) => lead.id !== draggableId);

      const destColumn = { ...newState.columns[destination.droppableId] };
      const updatedLead = { ...draggedLead, status: destination.droppableId };
      const destLeads = [...destColumn.leads];
      destLeads.splice(destination.index, 0, updatedLead);
      destColumn.leads = destLeads;

      newState.columns[source.droppableId] = sourceColumn;
      newState.columns[destination.droppableId] = destColumn;
      setState(newState);
      onStatsChange?.(computeStats(newState));

      await applyStatusUpdate(draggedLead, destination.droppableId);
    } catch (error) {
      console.error('Error updating inventory lead status:', error);
    }
  };

  const handleConfirmLost = async (selection: InventoryLeadLostReasonSelection) => {
    if (!pendingLostLead) return;
    const lead = pendingLostLead;
    setPendingLostLead(null);
    try {
      await applyStatusUpdate(lead, 'lost', selection);
    } catch (error) {
      console.error('Error marking inventory lead lost:', error);
    }
  };

  const handleCreateColumn = async () => {
    if (!newColumnTitle.trim() || !userInfo?.id) return;
    const columnId = `custom_${Date.now()}`;
    const color = generateRandomColor();
    const newColumn: CustomInventoryLeadColumn = {
      id: columnId,
      name: newColumnTitle.trim(),
      color,
      position: newColumnPosition,
      createdBy: userInfo.id,
    };

    try {
      await setDoc(doc(db, 'inventoryLeadColumns', columnId), newColumn);
      setCustomColumns((prev) => [...prev, newColumn]);
      await queryClient.invalidateQueries({ queryKey: ['inventoryLeadColumns'] });
    } catch (error) {
      console.error('Error saving inventory lead column:', error);
    }

    setNewColumnTitle('');
    setNewColumnPosition(0);
    setIsColumnDialogOpen(false);
  };

  const handleDeleteColumn = (columnId: string) => {
    setColumnToDelete(columnId);
    const firstAvailable = state.columnOrder.find((id) => id !== columnId);
    if (firstAvailable) setTargetColumnForLeads(firstAvailable);
    setIsDeleteColumnDialogOpen(true);
  };

  const handleViewDetails = useCallback((lead: InventoryLead) => {
    setSelectedLead(lead);
    setIsDetailDialogOpen(true);
  }, []);

  const handleDeleteLead = useCallback((lead: InventoryLead) => {
    setLeadToDelete(lead);
    setIsDeleteLeadDialogOpen(true);
  }, []);

  const confirmDeleteLead = async () => {
    if (!leadToDelete) return;

    try {
      const statusColumn = state.columns[leadToDelete.status || 'pendingList'];
      if (statusColumn) {
        setState((prev) => ({
          ...prev,
          columns: {
            ...prev.columns,
            [statusColumn.id]: {
              ...statusColumn,
              leads: statusColumn.leads.filter((lead) => lead.id !== leadToDelete.id),
            },
          },
        }));
      }

      if (selectedLead?.id === leadToDelete.id) {
        setIsDetailDialogOpen(false);
        setSelectedLead(null);
      }

      await deleteInventoryLead(leadToDelete.id);
      setIsDeleteLeadDialogOpen(false);
      setLeadToDelete(null);
    } catch (error) {
      console.error('Error deleting inventory lead:', error);
    }
  };

  const handleCloseDetailDialog = () => {
    setIsDetailDialogOpen(false);
    setSelectedLead(null);
  };

  const handleEditFromDetails = (lead: InventoryLead) => {
    setIsDetailDialogOpen(false);
    setSelectedLead(null);
    setEditingLead(lead);
    setIsEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setIsEditDialogOpen(false);
    setEditingLead(null);
  };

  const confirmDeleteColumn = async () => {
    if (!columnToDelete || !targetColumnForLeads) return;
    const columnToRemove = state.columns[columnToDelete];
    const leadsToMove = columnToRemove?.leads ?? [];

    try {
      if (!DEFAULT_COLUMNS.includes(columnToDelete)) {
        await deleteDoc(doc(db, 'inventoryLeadColumns', columnToDelete));
        setCustomColumns((prev) => prev.filter((col) => col.id !== columnToDelete));
        await queryClient.invalidateQueries({ queryKey: ['inventoryLeadColumns'] });
      }

      for (const lead of leadsToMove) {
        await updateInventoryLead({
          id: lead.id,
          updates: { status: targetColumnForLeads },
        });
      }
    } catch (error) {
      console.error('Error deleting inventory lead column:', error);
    }

    setIsDeleteColumnDialogOpen(false);
    setColumnToDelete(null);
    setTargetColumnForLeads('');
  };

  const mobileGroups = useMemo(
    () =>
      visibleColumnOrder
        .map((columnId) => {
          const column = state.columns[columnId];
          return { columnId, name: column?.name || columnId, leads: column?.leads || [] };
        })
        .filter((group) => group.leads.length > 0),
    [visibleColumnOrder, state.columns],
  );
  const mobileTotal = useMemo(
    () => mobileGroups.reduce((sum, group) => sum + group.leads.length, 0),
    [mobileGroups],
  );

  return (
    <Box>
      {isMobile ? (
        mobileTotal === 0 ? (
          <MobileRecordList isEmpty empty='No inventory leads.'>{null}</MobileRecordList>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {mobileGroups.map((group) => (
              <Box key={group.columnId}>
                <Box
                  sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                    bgcolor: 'background.default',
                    py: 0.75,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Typography
                    variant='caption'
                    sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}
                  >
                    {group.name}
                  </Typography>
                  <Typography variant='caption' color='text.secondary'>
                    {group.leads.length}
                  </Typography>
                </Box>
                <MobileRecordList>
                  {group.leads.map((lead) => {
                    const repName = resolveInventoryRepName(users, lead.assignedRepId);
                    return (
                      <MobileRecordCard
                        key={lead.id}
                        title={getContactName(lead.contactId)}
                        meta={getCompanyName(lead.companyId)}
                        detail={repName ? `Rep · ${repName}` : undefined}
                        status={{ tone: inventoryLeadStatusTone(group.columnId), label: group.name }}
                        onClick={() => handleViewDetails(lead)}
                      />
                    );
                  })}
                </MobileRecordList>
              </Box>
            ))}
          </Box>
        )
      ) : (
      <DragDropContext onDragEnd={onDragEnd}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            overflowX: 'auto',
            overflowY: 'hidden',
            pb: 2,
            gap: 2,
            scrollBehavior: 'smooth',
            '&::-webkit-scrollbar': { height: '8px' },
            '&::-webkit-scrollbar-track': {
              backgroundColor: alpha(theme.palette.text.primary, 0.05),
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: alpha(theme.palette.text.primary, 0.2),
              borderRadius: '4px',
              '&:hover': { backgroundColor: alpha(theme.palette.text.primary, 0.3) },
            },
            minHeight: '700px',
          }}
        >
          {visibleColumnOrder.map((columnId) => {
            const column = state.columns[columnId];
            if (!column) return null;
            const isDefaultColumn = isDefaultInventoryLeadStatus(columnId);

            return (
              <Box
                key={columnId}
                sx={{
                  width: { xs: 250, sm: 280, md: 300 },
                  minWidth: { xs: 250, sm: 280, md: 300 },
                  position: 'relative',
                  flexShrink: 0,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <InventoryLeadList
                  title={column.name}
                  leads={column.leads}
                  status={columnId}
                  color={column.color}
                  getContactName={getContactName}
                  getCompanyName={getCompanyName}
                  onViewDetails={handleViewDetails}
                  onDeleteLead={handleDeleteLead}
                  onDeleteColumn={!isDefaultColumn ? handleDeleteColumn : undefined}
                  isDefaultColumn={isDefaultColumn}
                  columnName={column.name}
                />
              </Box>
            );
          })}
        </Box>
      </DragDropContext>
      )}

      {selectedLead && (
        <InventoryLeadDetailDialog
          open={isDetailDialogOpen}
          onClose={handleCloseDetailDialog}
          lead={selectedLead}
          contactName={getContactName(selectedLead.contactId)}
          companyName={getCompanyName(selectedLead.companyId)}
          onEdit={handleEditFromDetails}
          columnName={state.columns[selectedLead.status || '']?.name}
          columnColor={state.columns[selectedLead.status || '']?.color}
        />
      )}

      <InventoryLeadFormDialog
        open={isEditDialogOpen}
        onClose={handleCloseEditDialog}
        selectedLead={editingLead}
      />

      <InventoryLeadLostReasonDialog
        open={!!pendingLostLead}
        onClose={() => setPendingLostLead(null)}
        onConfirm={handleConfirmLost}
        leadLabel={
          pendingLostLead
            ? [
                pendingLostLead.leadNumber,
                getContactName(pendingLostLead.contactId),
                getCompanyName(pendingLostLead.companyId),
              ]
                .filter(Boolean)
                .join(' · ')
            : undefined
        }
      />

      <Dialog open={isColumnDialogOpen} onClose={() => setIsColumnDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Column</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              label="Column Title"
              fullWidth
              value={newColumnTitle}
              onChange={(e) => setNewColumnTitle(e.target.value)}
              margin="normal"
              required
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Position</InputLabel>
              <Select
                value={newColumnPosition}
                onChange={(e) => setNewColumnPosition(Number(e.target.value))}
                label="Position"
              >
                {state.columnOrder.map((_, index) => (
                  <MenuItem key={index} value={index}>
                    {index === 0
                      ? 'First'
                      : `After ${state.columns[state.columnOrder[index - 1]]?.name}`}
                  </MenuItem>
                ))}
                <MenuItem value={state.columnOrder.length}>Last</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsColumnDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateColumn}
            variant="contained"
            disabled={!newColumnTitle.trim()}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isDeleteColumnDialogOpen}
        onClose={() => setIsDeleteColumnDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Column</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" gutterBottom>
              Are you sure you want to delete the column "
              {columnToDelete ? state.columns[columnToDelete]?.name : ''}"?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              All leads in this column will be moved to another column.
            </Typography>
            <FormControl fullWidth margin="normal">
              <InputLabel>Move leads to</InputLabel>
              <Select
                value={targetColumnForLeads}
                onChange={(e) => setTargetColumnForLeads(e.target.value)}
                label="Move leads to"
              >
                {state.columnOrder
                  .filter((id) => id !== columnToDelete)
                  .map((columnId) => (
                    <MenuItem key={columnId} value={columnId}>
                      {state.columns[columnId]?.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteColumnDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDeleteColumn} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isDeleteLeadDialogOpen}
        onClose={() => {
          setIsDeleteLeadDialogOpen(false);
          setLeadToDelete(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Lead</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" gutterBottom>
              Are you sure you want to delete this inventory lead?
            </Typography>
            {leadToDelete && (
              <Typography variant="body2" color="text.secondary">
                Lead:{' '}
                {[
                  leadToDelete.leadNumber,
                  getContactName(leadToDelete.contactId),
                  getCompanyName(leadToDelete.companyId),
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </Typography>
            )}
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              This action cannot be undone.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setIsDeleteLeadDialogOpen(false);
              setLeadToDelete(null);
            }}
          >
            Cancel
          </Button>
          <Button onClick={() => void confirmDeleteLead()} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InventoryLeadsBoard;
